<template>
  <div class="my-invest-list">
    <!-- 空状态 -->
    <div class="empty-state" v-if="videoList.length === 0 && !loading">
      <div class="empty-icon">
        <i class="fas fa-film"></i>
      </div>
      <h3>{{ $t('_wdtg._wjl') }}</h3>
    </div>

    <!-- 列表 -->
    <div v-else>
      <div v-for="video in videoList" :key="video.id" class="invest-card">
        <!-- 封面 -->
        <div class="invest-cover">
          <img :src="video.img" :alt="video.title" class="cover-img" />
          <!-- 封面状态角标 -->
          <div class="cover-badge" v-if="video.status === 1">
            <span class="badge badge-pending">待支付</span>
          </div>
          <div class="cover-badge" v-else-if="video.status === 3">
            <span class="badge badge-active">进行中</span>
          </div>
          <div class="cover-badge" v-else-if="video.status === 2">
            <span class="badge badge-completed">已完成</span>
          </div>
        </div>

        <!-- 信息区域 -->
        <div class="invest-info-wrap">
          <div class="invest-info">
            <div class="info-header">
              <h4 class="invest-title">{{ video.title }}</h4>
              <span class="invest-code">#{{ video.code }}</span>
            </div>

            <!-- 统计信息 -->
            <div class="stats-grid">
              <div class="stat-item">
                <span class="stat-label">{{ $t('_wdtg._tzje') }}</span>
                <span class="stat-value amount">${{ video.amount }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('_wdtg._zsy') }}</span>
                <span class="stat-value profit">+${{ video.income || 0 }}</span>
              </div>
              <div class="stat-item" v-if="video.status == 3">
                <span class="stat-label">{{ $t('_bq._zrsy') }}</span>
                <span class="stat-value profit">+${{ video.last_income || 0 }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('_bq._tzzq') }}</span>
                <span class="stat-value">{{ video.days || 0 }} 天</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">{{ $t('_wdtg._cysj') }}</span>
                <span class="stat-value time">{{ formatDate(video.create_time) }}</span>
              </div>
            </div>

            <!-- 状态 -->
            <div class="status-row" v-if="video.status === 1">
              <div class="status-badge status-pending">
                <i class="fas fa-clock"></i>
                {{ $t('_wdtg._qzf') }}
              </div>
              <div class="status-detail">
                <span class="status-label">{{ $t('_bq._zfjz') }}</span>
                <span class="status-time">{{ video.last_pay || '--' }}</span>
              </div>
            </div>
            <div class="status-row" v-if="video.status === 3">
              <div class="status-badge status-active">
                <i class="fas fa-play-circle"></i>
                {{ $t('_wdtg._jxz') }}
              </div>
            </div>
            <div class="status-row" v-if="video.status === 2">
              <div class="status-badge status-completed">
                <i class="fas fa-check-circle"></i>
                {{ $t('_wd._ywc') }}
              </div>
            </div>
          </div>

          <!-- 操作按钮 -->
          <div class="action-area" v-if="video.status === 1">
            <button class="btn-pay" @click="onPay(video)">
              <i class="fas fa-credit-card"></i>
              {{ $t('_bq._qzf') }}
            </button>
          </div>

          <!-- 收益明细 -->
          <div class="income-section">
            <!-- 昨日待结算 -->
            <div class="income-box income-pending" v-if="video.zr_income_status == 3">
              <div class="income-header">
                <span class="income-title">📋 {{ $t('_wdtg._djs') }}</span>
                <span class="income-date">[{{ video.zr_date || '--' }}]</span>
              </div>
              <div class="income-grid">
                <div class="income-item">
                  <span class="income-label">{{ $t('_wdtg._zrsy') }}</span>
                  <span class="income-value pending-amount">${{ video.zr_income || 0 }}</span>
                </div>
                <div class="income-item">
                  <span class="income-label">{{ $t('_wdtg._dbl') }}</span>
                  <span class="income-value">{{ video.zr_buy_num || 0 }}</span>
                </div>
                <div class="income-item">
                  <span class="income-label">{{ $t('_wdtg._bfl') }}</span>
                  <span class="income-value">{{ video.zr_play_num || 0 }}</span>
                </div>
                <div class="income-item">
                  <span class="income-label">{{ $t('_wdtg._wdsy') }}</span>
                  <span class="income-value profit">${{ video.zr_real_income || 0 }}</span>
                </div>
                <div class="income-item">
                  <span class="income-label">{{ $t('_wdtg._bqffc') }}</span>
                  <span class="income-value profit">${{ video.zr_store_income || 0 }}</span>
                </div>
              </div>
              <button class="btn-transfer" :class="{ 'btn-urgent': video.zr_expired_hours < 24 }" @click="onTransfer(video)">
                <i class="fas fa-exchange-alt"></i>
                {{ $t('_ggtg._qhz') }}
                <span v-if="video.zr_expired_hours < 24" class="urgent-tag">紧急</span>
              </button>
            </div>

            <!-- 今日结算 -->
            <div class="income-box income-settled" v-if="video.status == 3 && video.jt_income_status > 0 && video.zr_income_status != 3">
              <div class="income-header">
                <span class="income-title">✅ {{ $t('_wdtg._mr0djs') }}</span>
              </div>
              <div class="income-grid">
                <div class="income-item">
                  <span class="income-label">{{ $t('_wdtg._jrsy') }}</span>
                  <span class="income-value profit">${{ video.jt_income || 0 }}</span>
                </div>
                <div class="income-item">
                  <span class="income-label">{{ $t('_wdtg._dbl') }}</span>
                  <span class="income-value">{{ video.jt_buy_num || 0 }}</span>
                </div>
                <div class="income-item">
                  <span class="income-label">{{ $t('_wdtg._bfl') }}</span>
                  <span class="income-value">{{ video.jt_play_num || 0 }}</span>
                </div>
                <div class="income-item">
                  <span class="income-label">{{ $t('_wdtg._wdsy') }}</span>
                  <span class="income-value profit">${{ video.jt_real_income || 0 }}</span>
                </div>
                <div class="income-item">
                  <span class="income-label">{{ $t('_wdtg._bqffc') }}</span>
                  <span class="income-value profit">${{ video.jt_store_income || 0 }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载更多 -->
    <div class="load-more">
      <button class="load-btn" @click="loadMore" v-if="hasMore" :disabled="loading">
        <i class="fas fa-spinner fa-spin" v-if="loading"></i>
        {{ loading ? $t('_a._jzz') : $t('_a._jzgd') }}
      </button>
      <div class="no-more" v-else>{{ $t('_wdtg._mygd') }}</div>
    </div>

    <!-- 购买弹窗 -->
    <InvestBuy ref="buy" @success="onSuccess" />
    <AdvTransfer ref="transfer" @success="onTransferSuccess" />
  </div>
</template>

<script>
import AdvTransfer from '@/components/adv/AdvTransfer.vue'
import InvestBuy from './InvestBuy.vue'

export default {
  components: {
    InvestBuy,
    AdvTransfer
  },
  data() {
    return {
      loading: false,
      hasMore: true,
      page: 1,
      size: 4,
      videoList: []
    }
  },
  created() {
    this.getList()
  },
  methods: {
    onPay(item) {
      this.$refs.buy.open(item, item.code)
    },

    onSuccess() {
      this.getList(1)
    },

    onTransfer(item) {
      this.$refs.transfer.open(item)
    },

    onTransferSuccess() {
      this.getList(1)
    },

    getList(page = 0) {
      if (page > 0) {
        this.videoList = []
        this.page = page
        this.hasMore = true
      }
      if (this.loading) return

      this.loading = true

      this.$api.videoInvest
        .getMyInvestList({
          page: this.page,
          size: this.size
        })
        .then((res) => {
          this.loading = false
          this.page++
          if (res.data.length === 0) {
            this.hasMore = false
            return
          }
          res.data.forEach((item) => {
            this.videoList.push(item)
          })
          this.hasMore = res.data.length >= this.size
        })
        .catch(() => {
          this.loading = false
        })
    },

    formatDate(date) {
      if (!date) return '--'
      const d = new Date(date)
      return d.toLocaleDateString('zh-CN') + ' ' + d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    },

    loadMore() {
      this.getList()
    }
  }
}
</script>

<style scoped>
/* ============================================================
   我参与的版权投资 - 深灰主题
   ============================================================ */

.my-invest-list {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0;
  color: #e8edf2;
}

/* ============================================================
   空状态
   ============================================================ */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: #1a1f26;
  border-radius: 14px;
  border: 2px dashed #2a3038;
}

.empty-icon {
  font-size: 56px;
  color: #3a4652;
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 22px;
  color: #e8edf2;
  margin: 0;
}

/* ============================================================
   卡片
   ============================================================ */
.invest-card {
  display: flex;
  background: #1a1f26;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 24px;
  border: 1px solid #2a3038;
  transition: all 0.35s ease;
}

.invest-card:hover {
  border-color: #ffb300;
  /* transform: translateY(-4px); */
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.35);
}

/* ============================================================
   封面
   ============================================================ */
.invest-cover {
  flex: 0 0 220px;
  height: 200px;
  overflow: hidden;
  background: #0a0d0f;
  position: relative;
}

.cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.invest-card:hover .cover-img {
  transform: scale(1.05);
}

/* ============================================================
   封面角标
   ============================================================ */
.cover-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
}

.badge {
  display: inline-block;
  padding: 5px 16px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.badge-pending {
  background: rgba(255, 152, 0, 0.85);
  color: #fff;
}

.badge-active {
  background: rgba(38, 166, 154, 0.85);
  color: #fff;
}

.badge-completed {
  background: rgba(76, 175, 80, 0.85);
  color: #fff;
}

/* ============================================================
   信息区域
   ============================================================ */
.invest-info-wrap {
  flex: 1;
  padding: 16px 22px 16px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  position: relative;
}

.invest-info {
  flex: 1;
}

/* 标题行 */
.info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.invest-title {
  font-size: 19px;
  font-weight: 700;
  color: #e8edf2;
  margin: 0;
  line-height: 1.3;
}

.invest-code {
  font-size: 14px;
  color: #5a6672;
  background: #0a0d0f;
  padding: 4px 14px;
  border-radius: 10px;
  font-family: monospace;
}

/* ============================================================
   统计信息
   ============================================================ */
.stats-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  padding: 10px 16px;
  background: #0a0d0f;
  border-radius: 10px;
  border: 1px solid #2a3038;
  margin-bottom: 12px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-label {
  font-size: 12px;
  color: #5a6672;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #e8edf2;
}

.stat-value.amount {
  color: #ffb300;
}

.stat-value.profit {
  color: #26a69a;
}

.stat-value.time {
  font-size: 14px;
  color: #6a7a8a;
  font-weight: 500;
}

/* ============================================================
   状态
   ============================================================ */
.status-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
}

.status-pending {
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
  border: 1px solid rgba(255, 152, 0, 0.2);
}

.status-active {
  background: rgba(38, 166, 154, 0.15);
  color: #26a69a;
  border: 1px solid rgba(38, 166, 154, 0.2);
}

.status-completed {
  background: rgba(76, 175, 80, 0.15);
  color: #4caf50;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.status-detail {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-label {
  font-size: 14px;
  color: #6a7a8a;
}

.status-time {
  font-size: 14px;
  color: #e8edf2;
  font-weight: 500;
}

/* ============================================================
   操作按钮
   ============================================================ */
.action-area {
  display: flex;
  justify-content: flex-end;
  padding: 10px 0 8px;
  border-top: 1px solid #2a3038;
  margin-top: 4px;
}

.btn-pay {
  padding: 8px 28px;
  border: none;
  border-radius: 30px;
  background: linear-gradient(135deg, #ffb300, #f57c00);
  color: #0b0d0f;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-pay:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(255, 179, 0, 0.3);
}

/* ============================================================
   收益明细
   ============================================================ */
.income-section {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.income-box {
  flex: 1;
  min-width: 200px;
  padding: 14px 18px;
  border-radius: 12px;
  border: 1px solid #2a3038;
  background: #0a0d0f;
}

.income-pending {
  border-color: rgba(255, 87, 34, 0.3);
  background: rgba(255, 87, 34, 0.04);
}

.income-settled {
  border-color: rgba(38, 166, 154, 0.3);
  background: rgba(38, 166, 154, 0.04);
}

.income-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.income-title {
  font-size: 15px;
  font-weight: 600;
  color: #e8edf2;
}

.income-date {
  font-size: 14px;
  color: #5a6672;
}

.income-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
}

.income-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.income-label {
  font-size: 11px;
  color: #5a6672;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.income-value {
  font-size: 15px;
  font-weight: 600;
  color: #e8edf2;
}

.income-value.profit {
  color: #26a69a;
}

.income-value.pending-amount {
  color: #ff5722;
}

/* ============================================================
   划转按钮
   ============================================================ */
.btn-transfer {
  margin-top: 10px;
  padding: 7px 20px;
  border: none;
  border-radius: 20px;
  background: linear-gradient(135deg, #ffb300, #f57c00);
  color: #0b0d0f;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-transfer:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(255, 179, 0, 0.3);
}

.btn-transfer.btn-urgent {
  background: linear-gradient(135deg, #ef5350, #c62828);
  color: #fff;
}

.btn-transfer.btn-urgent:hover {
  box-shadow: 0 4px 20px rgba(239, 83, 80, 0.3);
}

.urgent-tag {
  background: rgba(255, 255, 255, 0.2);
  padding: 1px 10px;
  border-radius: 10px;
  font-size: 11px;
}

/* ============================================================
   加载更多
   ============================================================ */
.load-more {
  text-align: center;
  padding: 30px 0 10px;
}

.load-btn {
  padding: 10px 40px;
  border: 1px solid #2a3038;
  border-radius: 30px;
  background: transparent;
  color: #8a949f;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.load-btn:hover:not(:disabled) {
  border-color: #ffb300;
  color: #e8edf2;
}

.load-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.load-btn i {
  font-size: 16px;
}

.no-more {
  color: #3a4652;
  font-size: 15px;
  letter-spacing: 1px;
}

/* ============================================================
   响应式
   ============================================================ */
@media (max-width: 820px) {
  .invest-card {
    flex-direction: column;
  }

  .invest-cover {
    flex: 0 0 auto;
    height: 170px;
    width: 100%;
  }

  .invest-info-wrap {
    padding: 14px 16px 14px;
  }

  .invest-title {
    font-size: 17px;
  }

  .stats-grid {
    gap: 6px 16px;
    padding: 8px 14px;
  }

  .stat-value {
    font-size: 15px;
  }

  .income-section {
    flex-direction: column;
  }

  .income-box {
    min-width: auto;
  }
}

@media (max-width: 480px) {
  .invest-cover {
    height: 130px;
  }

  .invest-info-wrap {
    padding: 12px 14px 12px;
  }

  .invest-title {
    font-size: 16px;
  }

  .invest-code {
    font-size: 13px;
  }

  .stats-grid {
    gap: 4px 12px;
    padding: 6px 10px;
  }

  .stat-value {
    font-size: 14px;
  }

  .stat-label {
    font-size: 10px;
  }

  .status-badge {
    font-size: 13px;
    padding: 4px 14px;
  }

  .status-label,
  .status-time {
    font-size: 13px;
  }

  .btn-pay {
    font-size: 14px;
    padding: 6px 20px;
    width: 100%;
    justify-content: center;
  }

  .action-area {
    justify-content: stretch;
  }

  .income-box {
    padding: 10px 14px;
  }

  .income-title {
    font-size: 14px;
  }

  .income-value {
    font-size: 14px;
  }

  .btn-transfer {
    width: 100%;
    justify-content: center;
  }

  .load-btn {
    padding: 8px 24px;
    font-size: 14px;
  }

  .badge {
    font-size: 11px;
    padding: 3px 12px;
  }
}
</style>